import React from 'react';

import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Menu,
  Typography,
} from 'components';
import { More } from 'iconsax-react';

import { styled, useTheme } from 'third-party';

import { getAvatarName } from 'utils/helper';

const PointerBox = styled.div<{ $isPointer: boolean }>`
  cursor: ${props => (props.$isPointer ? 'pointer' : 'default')};
  user-select: none;
`;

type Props<TItem> = {
  item: TItem;
  title: string;
  width?: string;
  height?: string;
  showAvatar?: boolean;
  avatarUrl?: string;
  description?: string | null;
  descriptionMaxLines?: number;
  subTitle?: React.ReactNode;
  menuItems?: (item: TItem, handleClose: () => void) => React.ReactNode;
  cardActions?: (item: TItem) => React.ReactNode;
  onClick?: (item: TItem) => void;
  children?: React.ReactNode;
  stripeText?: string;
  badgeText?: string;
  alertText?: string;
  greyedOut?: boolean;
  highlightQuery?: string;
};

const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return text;
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} style={{ backgroundColor: '#ffff00' }}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

export const CardBox = <TItem,>({
  item,
  title,
  subTitle,
  description,
  descriptionMaxLines = 2,
  width,
  height,
  showAvatar = true,
  avatarUrl,
  onClick,
  menuItems,
  cardActions,
  children,
  stripeText,
  badgeText,
  alertText,
  greyedOut = false,
  highlightQuery,
}: Props<TItem>) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menu = menuItems?.(item, handleClose);
  const accentColor = stripeText
    ? theme.custom.palette.secondary800
    : theme.custom.palette.primary900;

  return (
    <Card
      sx={{
        position: 'relative',
        borderRadius: '12px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03)',
        overflow: 'hidden',
        bgcolor: greyedOut
          ? theme.custom.palette.grey50
          : theme.custom.palette.backgroundThemeLight,
        opacity: greyedOut ? 0.7 : 1,
        filter: greyedOut ? 'grayscale(1)' : 'none',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        '&:hover': greyedOut
          ? undefined
          : {
              boxShadow:
                '0 4px 12px rgba(0,0,0,0.07), 0 2px 6px rgba(0,0,0,0.04)',
              transform: 'translateY(-1px)',
            },
      }}
    >
      <PointerBox $isPointer={!!onClick} onClick={() => onClick?.(item)}>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          sx={{ width: width ?? 300, height: height ?? 220 }}
        >
          <CardHeader
            sx={{
              padding: '0.875rem 0.75rem 0.75rem 1rem',
              '& .MuiCardHeader-content': {
                overflow: 'hidden',
                minWidth: 0,
              },
              '& .MuiCardHeader-avatar': {
                marginRight: '0.75rem',
              },
              '& .MuiCardHeader-action': {
                marginTop: '-2px',
                marginRight: '-6px',
              },
            }}
            avatar={
              <Box>
                {showAvatar && !avatarUrl && (
                  <Avatar
                    sx={{
                      bgcolor: accentColor,
                      width: 42,
                      height: 42,
                      fontSize: '0.9375rem',
                      fontWeight: 700,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {getAvatarName(title)}
                  </Avatar>
                )}
                {showAvatar && avatarUrl && (
                  <Avatar src={avatarUrl} sx={{ width: 42, height: 42 }} />
                )}
              </Box>
            }
            action={
              menu && (
                <IconButton
                  aria-label="settings"
                  size="small"
                  sx={{
                    color: theme.custom.palette.grey400,
                    '&:hover': {
                      color: theme.custom.palette.grey600,
                      bgcolor: theme.custom.palette.grey100,
                    },
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    handleClick(e);
                  }}
                >
                  <More
                    variant="TwoTone"
                    size="20"
                    style={{ transform: 'rotate(90deg)' }}
                  />
                </IconButton>
              )
            }
            title={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="0.5rem"
              >
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: theme.custom.palette.darkText,
                    lineHeight: 1.4,
                  }}
                >
                  {highlightQuery
                    ? highlightText(title, highlightQuery)
                    : title}
                </Typography>
                {badgeText && (
                  <Chip
                    label={badgeText}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      height: '18px',
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      bgcolor: theme.custom.palette.grey100,
                      color: theme.custom.palette.grey600,
                      border: `1px solid ${theme.custom.palette.grey400}`,
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                )}
                {stripeText && (
                  <Chip
                    label={stripeText}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      height: '18px',
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      bgcolor: theme.custom.palette.warning50,
                      color: theme.custom.palette.warning600,
                      border: `1px solid ${theme.custom.palette.warning600}`,
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                )}
                {alertText && (
                  <Chip
                    label={alertText}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      height: '18px',
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      bgcolor: theme.custom.palette.error50,
                      color: theme.custom.palette.error600,
                      border: `1px solid ${theme.custom.palette.error600}`,
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                )}
              </Box>
            }
            subheader={
              subTitle && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme.custom.palette.subTittle,
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  {highlightQuery && typeof subTitle === 'string'
                    ? highlightText(subTitle, highlightQuery)
                    : subTitle}
                </Typography>
              )
            }
          />
          <Box
            sx={{
              height: '1px',
              bgcolor: theme.custom.palette.borderColor,
              mx: '1rem',
            }}
          />
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            justifyContent="space-between"
          >
            <CardContent
              sx={{ padding: '0.75rem 1rem 0.5rem 1rem !important' }}
            >
              {description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.custom.palette.truegray600,
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: descriptionMaxLines,
                    fontSize: '0.8125rem',
                    lineHeight: 1.55,
                  }}
                >
                  {highlightQuery && typeof description === 'string'
                    ? highlightText(description, highlightQuery)
                    : description}
                </Typography>
              )}
              {children}
            </CardContent>
            {cardActions && (
              <CardActions
                disableSpacing
                sx={{
                  px: '1rem',
                  py: '0.375rem',
                  borderTop: `1px solid ${theme.custom.palette.borderColor}`,
                  bgcolor: theme.custom.palette.grey50,
                }}
                onClick={e => e.stopPropagation()}
              >
                {cardActions(item)}
              </CardActions>
            )}
          </Box>
        </Box>
      </PointerBox>
      {menu && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {menu}
        </Menu>
      )}
    </Card>
  );
};
